import type { Notification } from './api';

export type NotificationTarget =
  | { kind: 'internal'; path: string }
  | { kind: 'external'; url: string };

type NotificationTargetInput = Pick<
  Notification,
  'action_url' | 'notification_type' | 'related_id' | 'related_entity_type' | 'metadata'
>;

/** entity id from metadata.<*_id> when related_id isn't the navigable record. */
function entityId(n: NotificationTargetInput): string | undefined {
  if (typeof n.related_id === 'string' && n.related_id) return n.related_id;
  const meta = n.metadata as Record<string, unknown> | null | undefined;
  const fromMeta = meta?.['support_request_id'] ?? meta?.['entity_id'];
  return typeof fromMeta === 'string' ? fromMeta : undefined;
}

export function getNotificationTarget(n: NotificationTargetInput): NotificationTarget | null {
  // 1) Explicit action_url is authoritative.
  if (n.action_url?.trim()) {
    const raw = n.action_url.trim();
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      return { kind: 'external', url: raw };
    }
    if (raw.startsWith('/app/')) return { kind: 'internal', path: raw };
    if (raw.startsWith('/')) return { kind: 'internal', path: `/app${raw}` };
    return { kind: 'internal', path: `/app/${raw}` };
  }

  // 2) Structured entity_type routing (preferred over the loose notification_type).
  switch (n.related_entity_type) {
    case 'support_request':
    case 'support_comment': {
      const id = entityId(n);
      return { kind: 'internal', path: id ? `/app/support/requests/${id}` : '/app/support/requests' };
    }
    default:
      break;
  }

  // 3) Fallback: legacy notification_type mapping.
  switch (n.notification_type) {
    case 'support_comment':
      return {
        kind: 'internal',
        path: n.related_id ? `/app/support/requests/${n.related_id}` : '/app/support/requests',
      };
    case 'classified_pending':
    case 'classified_approved':
    case 'classified_declined':
    case 'classified_removed':
      return {
        kind: 'internal',
        path: n.related_id ? `/app/classifieds/${n.related_id}` : '/app/classifieds',
      };
    case 'supplier_pending':
    case 'supplier_approved':
    case 'supplier_rejected':
      return {
        kind: 'internal',
        path: n.related_id ? `/app/suppliers/${n.related_id}` : '/app/suppliers',
      };
    case 'event_registration':
      return { kind: 'internal', path: '/app/events' };
    case 'access_invite_issued':
    case 'access_invite_cancelled':
    case 'access_invite_expiring':
      return {
        kind: 'internal',
        path: n.related_id ? `/app/access-invites?invite=${n.related_id}` : '/app/access-invites',
      };
    case 'gate_entry':
    case 'gate_exit':
    case 'gate_sos':
      return {
        kind: 'internal',
        path: n.related_id ? `/app/access-invites?tab=gate` : '/app/access-invites?tab=gate',
      };
    // The feed row only persists notification_type + related_id (not action_url /
    // related_entity_type), so this fallback is what routes a facility-booking tap in-app.
    case 'facility_booking_reminder':
    case 'facility_booking_cancelled':
    case 'facility_booking_approved':
    case 'facility_booking_rejected':
    case 'facility_booking_updated':
      return { kind: 'internal', path: '/app/facilities/my-bookings' };
    // Terms changed on a facility: deep-link to that facility so the member can review them.
    case 'facility_terms_updated':
      return { kind: 'internal', path: n.related_id ? `/app/facilities/${n.related_id}` : '/app/facilities' };
    default:
      return null;
  }
}
